import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { META_IDENTIFICADAS, nivelIdentificadas } from "@huayruro/shared";
import { EN_VIVO_MS, useApi, mutar } from "../lib/useApi";
import { solesCent } from "../lib/money";
import { navegar } from "../lib/ruta";
import { ahoraFecha } from "../lib/fecha-ui";
import { Card, KpiCard, Chip, Tabs, TabPill, TableHead, TableRow, Th, Td, EmptyState, Button, Input } from "../components/ui";
import type { SesionActiva } from "../lib/tipos";

// "Ventas y caja" (refresh visual): funde el panel por botica + el consolidado de cadena bajo una sola
// vista con SUB-TABS DE SCOPE. El scoping lo impone el server (super manda ?sucursal_id / cadena vía
// consolidado/*; no-super clavado a su sede). SOLO presentación + wiring de datos reales; sin acciones.

// Subsets de lo que devuelven los endpoints (no importamos tipos del api; solo los campos que pinta la vista).
type Identificadas = { ventas: number; identificadas: number; pct: number | null };
type HoyBotica = { sucursal_id: string; nombre: string; ventas_cent: number; num_tickets: number; ticket_promedio_cent: number; identificadas_30d: Identificadas };
type HoyCadena = { ventas_cent: number; num_tickets: number; ticket_promedio_cent: number; pct_yape: number; identificadas_30d: Identificadas };
type HoyResumen = { cadena: HoyCadena | null; boticas: HoyBotica[] };
type ResumenDia = { por_metodo: Record<string, number>; total_sistema_cent: number };
type SucursalItem = { id: string; nombre: string };
// /caja/cierres y /consolidado/cierres tienen formas distintas → un tipo laxo que cubre ambas y se normaliza.
type RawCierre = {
  id?: string;
  fecha: string;
  sucursal_id?: string;
  sucursal_nombre?: string;
  total_efectivo_cent?: number;
  total_yape_cent?: number;
  total_otros_cent?: number;
  total_sistema_cent?: number;
  diferencia_cent?: number;
  esperado_cent?: number;
  contado_cent?: number;
  yape_cent?: number;
  dif_cent?: number;
};
type VentaFeed = { id: string; fecha_hora: string; sucursal_nombre: string; items_resumen: string; metodo_pago: string; total_cent: number };

const METODO_LABEL: Record<string, string> = { efectivo: "Efectivo", yape: "Yape", plin: "Plin", tarjeta: "Tarjeta", transferencia: "Transferencia", otro: "Otro" };

const COLS_CIERRES = "100px 170px 1fr 1fr 1fr 190px";
const COLS_FEED = "70px 150px 1fr 100px 90px 120px 150px";

// YYYY-MM-DD → "Sáb 11 jul" (es-PE, sin puntos/comas). Mediodía para no rozar el borde de zona.
function fechaCorta(ymd: string): string {
  const d = new Date(`${ymd}T12:00:00`);
  if (Number.isNaN(d.getTime())) return ymd;
  const s = d.toLocaleDateString("es-PE", { weekday: "short", day: "numeric", month: "short" }).replace(/\./g, "").replace(",", "");
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// ISO → "4:28pm" (hora local Lima, mono en la tabla).
function horaCorta(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true, timeZone: "America/Lima" }).replace(/\s/g, "").toLowerCase();
}

// Resultado del cierre: dif=0 Cuadró (ok) / dif>0 Sobró (warn) / dif<0 Faltó (danger) + "ver caso →".
// El enlace a Casos solo para quien entra a Casos: al lector lo dejaba en un clic muerto (RC-8).
function resultadoCierre(dif: number, verCaso: boolean): ReactNode {
  if (dif === 0) return <Chip variant="ok">Cuadró</Chip>;
  if (dif > 0) return <Chip variant="warn">Sobró {solesCent(dif)}</Chip>;
  return (
    <span className="inline-flex items-center gap-2">
      <Chip variant="danger">Faltó {solesCent(Math.abs(dif))}</Chip>
      {verCaso && (
        <button type="button" onClick={() => navegar("casos")} className="text-[11.5px] font-medium text-link hover:text-link-hover hover:underline">
          ver caso →
        </button>
      )}
    </span>
  );
}

// KPI A1 "% de ventas identificadas" (expansión §2): de cada 10 ventas, en cuántas se supo quién
// compró. Gobierna toda la capacidad de recompra — sin este número, los avisos de WhatsApp y el RFM
// no tienen a quién escribirle. Se pinta contra la meta (≥30 % el mes 1, ≥50 % el mes 3) y SIEMPRE
// sale de las ventas reales de los últimos 30 días; si no hubo ventas, se dice eso y no un 0 %.
function KpiIdentificadas({ dato, cargando }: { dato: Identificadas | undefined; cargando: boolean }) {
  const pct = dato?.pct ?? null;
  const nivel = nivelIdentificadas(pct);
  const color =
    nivel === "meta3" ? "text-ok" : nivel === "meta1" ? "text-ink" : nivel === "bajo" ? "text-accent-ink" : "text-ink-3";
  return (
    <div className="flex flex-col gap-1.5 rounded-[12px] border border-line bg-card p-[16px_18px]">
      <span className="text-[12px] font-semibold text-ink-2">Ventas con cliente (30 días)</span>
      <span className={`text-[24px] font-bold tracking-[-0.01em] tabular-nums ${color}`}>
        {cargando ? "…" : pct === null ? <span className="text-[15px] font-medium text-ink-3">Sin ventas aún</span> : `${pct}%`}
      </span>
      <span className="text-[11.5px] text-ink-3">
        {pct === null
          ? `Meta: ${META_IDENTIFICADAS.mes1}% el primer mes`
          : `${dato?.identificadas ?? 0} de ${dato?.ventas ?? 0} · meta ${META_IDENTIFICADAS.mes1}% mes 1 · ${META_IDENTIFICADAS.mes3}% mes 3`}
      </span>
    </div>
  );
}

function LoadingInline({ que = "datos" }: { que?: string }) {
  return <p className="py-6 text-center text-[13px] text-ink-3">Cargando {que}…</p>;
}

function ErrorInline({ msg, onRetry }: { msg: string; onRetry: () => void }) {
  return (
    <div className="py-6 text-center">
      <p className="text-[13px] text-accent-ink">{msg}</p>
      <button type="button" onClick={onRetry} className="mt-2 text-[12.5px] font-medium text-link hover:text-link-hover hover:underline">
        Reintentar
      </button>
    </div>
  );
}

// Detalle de una venta (C-7, tramo 07 de huayruro-orden-y-mapa): qué productos llevaba. `GET /ventas/:id`
// existía sin ninguna pantalla; el botón «Ver» vive junto a «Anular», que es donde se decide si deshacerla.
type ItemDetalle = { id: string; nombre: string; cantidad_presentacion: number; cantidad: number; total_cent: number };
function DetalleVentaModal({ venta, onCerrar }: { venta: VentaFeed; onCerrar: () => void }) {
  const det = useApi<{ venta: { total_cent: number; metodo_pago: string; fecha_hora: string }; items: ItemDetalle[] }>(`/ventas/${venta.id}`, [venta.id]);

  useEffect(() => {
    const esc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCerrar();
    };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onCerrar]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-surface/40 p-4 backdrop-blur" onClick={onCerrar}>
      <div
        role="dialog"
        aria-label="Detalle de la venta"
        className="w-full max-w-md rounded-[14px] border border-line bg-card p-6 shadow-[0_10px_30px_rgba(36,29,26,0.25)]"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-[16.5px] font-bold text-ink">Detalle de la venta</h2>
        <p className="mt-1 text-[13px] text-ink-2">
          {venta.sucursal_nombre} · {horaCorta(venta.fecha_hora)} · {METODO_LABEL[venta.metodo_pago] ?? venta.metodo_pago}
        </p>
        {det.cargando || (!det.data && !det.error) ? (
          <p className="py-4 text-center text-[13px] text-ink-3">Cargando…</p>
        ) : det.error ? (
          <p className="mt-3 text-[12.5px] text-accent-ink">{det.error}</p>
        ) : (
          <ul className="mt-3 flex flex-col divide-y divide-line-row">
            {det.data!.items.map((it) => (
              <li key={it.id} className="flex items-baseline justify-between gap-3 py-2">
                <span className="min-w-0 text-[13px] text-ink">
                  <span className="tabular-nums text-ink-2">{it.cantidad_presentacion} ×</span> {it.nombre}
                </span>
                <span className="shrink-0 tabular-nums text-[13px] text-ink">{solesCent(it.total_cent)}</span>
              </li>
            ))}
            <li className="flex items-baseline justify-between gap-3 pt-2.5">
              <span className="text-[13px] font-bold text-ink">Total</span>
              <span className="tabular-nums text-[14px] font-bold text-ink">{solesCent(det.data!.venta.total_cent)}</span>
            </li>
          </ul>
        )}
        <div className="mt-5 flex">
          <Button variant="outline" onClick={onCerrar} className="flex-1 justify-center">
            Cerrar
          </Button>
        </div>
      </div>
    </div>
  );
}

// Anular una venta (§7.6). El endpoint existe desde E6.2 pero NINGUNA pantalla lo llamaba: el
// piloto arrancó el 2026-07-06 sin forma de anular desde la interfaz, y el smoke de E12 tuvo que
// hacerlo por curl. Vive acá, en el feed, porque es donde se ve la venta que hay que deshacer.
//
// Pide MOTIVO obligatorio a propósito: anular repone stock y mueve la caja del día, así que deja
// rastro de por qué. El server le pone su propio prefijo por request (la guarda contra la doble
// anulación del §7.6), así que reintentar no repone stock dos veces.
function AnularModal({
  venta,
  onListo,
  onCancelar,
}: {
  venta: VentaFeed;
  onListo: () => void;
  onCancelar: () => void;
}) {
  const [motivo, setMotivo] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const esc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancelar();
    };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onCancelar]);

  async function anular() {
    setEnviando(true);
    setError(null);
    try {
      await mutar(`/ventas/${venta.id}/anular`, { method: "POST", body: { motivo: motivo.trim() } });
      onListo();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
      setEnviando(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-surface/40 p-4 backdrop-blur">
      <div className="w-full max-w-md rounded-[14px] border border-line bg-card p-6 shadow-[0_10px_30px_rgba(36,29,26,0.25)]">
        <h2 className="text-[16.5px] font-bold text-ink">Anular esta venta</h2>
        <p className="mt-1 text-[13px] text-ink-2">
          {venta.items_resumen || "Venta"} · <span className="tabular-nums">{solesCent(venta.total_cent)}</span>
        </p>
        <p className="mt-3 text-[12.5px] text-ink-2">
          El stock vuelve al inventario y la caja del día se recalcula. Queda registrado quién la anuló y por qué.
        </p>
        <label className="mt-4 block text-[12.5px] font-semibold text-ink-2" htmlFor="motivo-anulacion">
          Motivo
        </label>
        <Input
          id="motivo-anulacion"
          className="mt-1"
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder="Ej. la clienta devolvió el producto"
          autoFocus
        />
        {error && <p className="mt-2 text-[12.5px] text-accent-ink">{error}</p>}
        <div className="mt-5 flex gap-2">
          <Button variant="outline" onClick={onCancelar} disabled={enviando} className="flex-1 justify-center">
            Cancelar
          </Button>
          {/* `primary` es el rojo de marca (#d6202f sobre blanco = 5,1:1): la variante destructiva
              del design system, sin inventar una nueva. */}
          <Button variant="primary" onClick={() => void anular()} disabled={enviando || motivo.trim().length < 3} className="flex-1 justify-center">
            {enviando ? "Anulando…" : "Anular venta"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function Dashboard({ sesion }: { sesion: SesionActiva }) {
  const esSuper = sesion.usuario.rol === "super_admin";
  // Esta vista la ve también el `lector_reportes`, y `POST /ventas/:id/anular` es adminOSuper: sin
  // este candado el lector vería un botón que el server le va a negar con 403.
  const puedeAnular = esSuper || sesion.usuario.rol === "admin_sucursal";
  const esEncargado = sesion.usuario.rol === "admin_sucursal";
  const [porAnular, setPorAnular] = useState<VentaFeed | null>(null);
  const [verVenta, setVerVenta] = useState<VentaFeed | null>(null);
  const [scopeId, setScopeId] = useState<string | null>(null); // null = "Toda la cadena" (solo super)
  const scopeCadena = esSuper && scopeId === null;

  // Un solo /hoy/resumen: trae cadena (super, con pct_yape) + todas las boticas del alcance
  // (venta/tickets/ticket-prom). admin/lector → cadena=null + [su botica].
  // Resumen, caja del día y «Últimas ventas» se ponen al día solos cada 30 s (Kevin, 8-oct-2026).
  const resumen = useApi<HoyResumen>("/hoy/resumen", [], { cadaMs: EN_VIVO_MS });
  // Tabs de scope (solo super): "Toda la cadena" + una por sucursal (orden del server).
  const sucursales = useApi<{ sucursales: SucursalItem[] }>(esSuper ? "/sucursales" : null);

  // %Yape por botica NO viene en /hoy por botica → se deriva de /caja/dia (yape/total de HOY).
  const cajaDiaPath = scopeCadena ? null : esSuper ? `/caja/dia?sucursal_id=${scopeId}` : `/caja/dia`;
  const cajaDia = useApi<ResumenDia>(cajaDiaPath, [], { cadaMs: EN_VIVO_MS });
  // Cierres: cadena → /consolidado/cierres (super); botica → /caja/cierres (ahora trae sucursal_nombre).
  const cierresPath = scopeCadena ? "/consolidado/cierres" : esSuper ? `/caja/cierres?sucursal_id=${scopeId}` : `/caja/cierres`;
  const cierres = useApi<{ cierres: RawCierre[] }>(cierresPath);
  // Feed: super sin sucursal_id = cadena; con sucursal_id = esa; admin/lector = suya.
  const ventasPath = scopeCadena ? "/ventas" : esSuper ? `/ventas?sucursal_id=${scopeId}` : `/ventas`;
  const feed = useApi<{ ventas: VentaFeed[] }>(ventasPath, [], { cadaMs: EN_VIVO_MS });

  const boticas = resumen.data?.boticas ?? [];
  const cadena = resumen.data?.cadena ?? null;
  const boticaSel = esSuper ? (scopeId ? boticas.find((b) => b.sucursal_id === scopeId) : undefined) : boticas[0];

  const ventasCent = scopeCadena ? cadena?.ventas_cent ?? 0 : boticaSel?.ventas_cent ?? 0;
  const tickets = scopeCadena ? cadena?.num_tickets ?? 0 : boticaSel?.num_tickets ?? 0;
  const ticketProm = scopeCadena ? cadena?.ticket_promedio_cent ?? 0 : boticaSel?.ticket_promedio_cent ?? 0;
  const identificadas = scopeCadena ? cadena?.identificadas_30d : boticaSel?.identificadas_30d;

  // %Yape: cadena real de /hoy; botica derivado de /caja/dia (null si no hay ventas hoy → "próximamente").
  let yapeNode: ReactNode;
  if (scopeCadena) {
    yapeNode = `${cadena?.pct_yape ?? 0}%`;
  } else if (cajaDia.cargando) {
    yapeNode = <span className="text-[15px] font-medium text-ink-3">…</span>;
  } else {
    const total = cajaDia.data?.total_sistema_cent ?? 0;
    const y = cajaDia.data?.por_metodo?.["yape"] ?? 0;
    yapeNode = total > 0 ? `${Math.round((y / total) * 100)}%` : <span className="text-[15px] font-medium text-ink-3">Próximamente</span>;
  }
  const kpi = (v: ReactNode): ReactNode => (resumen.cargando ? "…" : v);

  const cierreRows = (cierres.data?.cierres ?? []).map((c) => {
    const dif = c.dif_cent ?? c.diferencia_cent ?? 0;
    return {
      key: c.id ?? `${c.fecha}-${c.sucursal_id ?? ""}`,
      fecha: c.fecha,
      botica: c.sucursal_nombre ?? "—",
      esperado: c.esperado_cent ?? c.total_sistema_cent ?? 0,
      contado: c.contado_cent ?? (c.total_efectivo_cent ?? 0) + (c.total_yape_cent ?? 0) + (c.total_otros_cent ?? 0),
      yape: c.yape_cent ?? c.total_yape_cent ?? 0,
      dif,
    };
  });
  const ventasRows = feed.data?.ventas ?? [];

  return (
    <div className="flex flex-col gap-[18px]">
      {/* Scope: filtra TODA la vista. Solo el super (cadena) elige; el resto ve solo su botica → sin tabs. */}
      {esSuper && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Tabs className="flex-wrap">
            <TabPill active={scopeCadena} onClick={() => setScopeId(null)}>
              Toda la cadena
            </TabPill>
            {(sucursales.data?.sucursales ?? []).map((s) => (
              <TabPill key={s.id} active={scopeId === s.id} onClick={() => setScopeId(s.id)}>
                {s.nombre}
              </TabPill>
            ))}
          </Tabs>
        </div>
      )}

      {/* 4 KPIs del scope */}
      {resumen.error ? (
        <Card>
          <ErrorInline msg={resumen.error} onRetry={resumen.recargar} />
        </Card>
      ) : (
        <div className="grid grid-cols-2 gap-[14px] xl:grid-cols-5">
          <KpiCard label="Ventas de hoy" value={kpi(solesCent(ventasCent))} />
          <KpiCard label="Tickets" value={kpi(String(tickets))} />
          <KpiCard label="Ticket promedio" value={kpi(solesCent(ticketProm))} />
          <KpiCard label="Cobrado por Yape" value={kpi(yapeNode)} />
          <KpiIdentificadas dato={identificadas} cargando={resumen.cargando} />
        </div>
      )}

      {/* Cierres de caja */}
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-[13.5px] font-bold text-ink">Cierres de caja</h2>
            <p className="mt-0.5 text-[12.5px] text-ink-2">Cada cierre compara el efectivo que debía haber con lo que se contó. El de hoy aparece cuando cada botica cierre.</p>
          </div>
          {/* Solo a quien cierra: al lector lo llevaba a una Caja sin formulario (RC-8), y el dueño mira
              la caja de cada botica por la píldora «Caja» (la cierra su encargado). Tampoco cuando la
              caja de hoy ya está cerrada (tramo 04, Kevin): llevaba a un cierre que el server rechaza. */}
          {esEncargado && cierres.data && !cierres.data.cierres.some((c) => c.fecha === ahoraFecha()) && (
            <Button variant="outline" size="sm" onClick={() => navegar("caja")}>
              Cerrar caja del día
            </Button>
          )}
        </div>
        {cierres.cargando ? (
          <LoadingInline que="cierres" />
        ) : cierres.error ? (
          <ErrorInline msg={cierres.error} onRetry={cierres.recargar} />
        ) : cierreRows.length === 0 ? (
          <EmptyState className="mt-3" title="Aún no hay cierres" subtitle="El de hoy aparece cuando cada botica cierre su caja." />
        ) : (
          <div className="mt-3">
            <TableHead cols={COLS_CIERRES}>
              <Th>Fecha</Th>
              <Th>Botica</Th>
              <Th align="right">Efectivo esperado</Th>
              <Th align="right">Efectivo contado</Th>
              <Th align="right">Yape</Th>
              <Th>Resultado</Th>
            </TableHead>
            {cierreRows.map((r) => (
              <TableRow key={r.key} cols={COLS_CIERRES}>
                <Td className="text-[12.5px] text-ink-2">{fechaCorta(r.fecha)}</Td>
                <Td className="truncate text-[13px] font-semibold text-ink">{r.botica}</Td>
                <Td align="right" className="tabular-nums text-[13px] text-ink">{solesCent(r.esperado)}</Td>
                <Td align="right" className="tabular-nums text-[13px] text-ink">{solesCent(r.contado)}</Td>
                <Td align="right" className="tabular-nums text-[13px] text-ink-2">{solesCent(r.yape)}</Td>
                <Td>{resultadoCierre(r.dif, puedeAnular)}</Td>
              </TableRow>
            ))}
          </div>
        )}
      </Card>

      {/* Últimas ventas (feed) */}
      <Card>
        <h2 className="text-[13.5px] font-bold text-ink">Últimas ventas</h2>
        <p className="mt-0.5 text-[12.5px] text-ink-2">Cada venta del mostrador queda registrada, con o sin internet.</p>
        {feed.cargando ? (
          <LoadingInline que="ventas" />
        ) : feed.error ? (
          <ErrorInline msg={feed.error} onRetry={feed.recargar} />
        ) : ventasRows.length === 0 ? (
          <EmptyState className="mt-3" title="Sin ventas todavía" subtitle="Las ventas del mostrador aparecen aquí apenas se registran." />
        ) : (
          <>
            <div className="mt-3">
              <TableHead cols={COLS_FEED}>
                <Th>Hora</Th>
                <Th>Botica</Th>
                <Th>Ítems</Th>
                <Th>Medio</Th>
                <Th align="right">Total</Th>
                <Th>Sync</Th>
                <Th>{puedeAnular ? "Acción" : ""}</Th>
              </TableHead>
              {ventasRows.map((v) => (
                <TableRow key={v.id} cols={COLS_FEED}>
                  <Td className="font-mono text-[11.5px] text-ink-2">{horaCorta(v.fecha_hora)}</Td>
                  <Td className="truncate text-[13px] font-semibold text-ink">{v.sucursal_nombre}</Td>
                  <Td className="overflow-hidden truncate text-[12.5px] text-ink-2">{v.items_resumen || "—"}</Td>
                  <Td>
                    <Chip variant={v.metodo_pago === "yape" ? "yape" : "neutral"}>{METODO_LABEL[v.metodo_pago] ?? v.metodo_pago}</Chip>
                  </Td>
                  <Td align="right" className="tabular-nums text-[13px] text-ink">{solesCent(v.total_cent)}</Td>
                  <Td>
                    <Chip variant="neutral">Sincronizada</Chip>
                  </Td>
                  <Td>
                    {/* «Ver» (C-7) y «Anular» son de Encargado y Dueño: el servidor cierra los dos a esos roles. */}
                    {puedeAnular && (
                      <div className="flex gap-1.5">
                        <Button variant="outline" size="sm" onClick={() => setVerVenta(v)} aria-label={`Ver la venta de las ${horaCorta(v.fecha_hora)}`}>
                          Ver
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setPorAnular(v)}>
                          Anular
                        </Button>
                      </div>
                    )}
                  </Td>
                </TableRow>
              ))}
            </div>
            {/* La cola offline es local del equipo del mostrador; el panel lee online → todo lo listado ya llegó al server. */}
            <p className="mt-2.5 text-[11.5px] text-ink-3-alt">
              Todo lo que ves aquí ya llegó al servidor. “Por sincronizar” es un estado del equipo del mostrador; verlo por equipo en el panel, próximamente.
            </p>
          </>
        )}
      </Card>

      {verVenta && <DetalleVentaModal venta={verVenta} onCerrar={() => setVerVenta(null)} />}
      {porAnular && (
        <AnularModal
          venta={porAnular}
          onCancelar={() => setPorAnular(null)}
          onListo={() => {
            setPorAnular(null);
            // El feed solo lista 'completada', así que la fila desaparece sola. Los KPI del día y la
            // caja también cambian con la anulación: se recargan los tres, no solo el feed.
            feed.recargar();
            resumen.recargar();
            cajaDia.recargar();
          }}
        />
      )}
    </div>
  );
}
